import React, { useEffect, useState } from 'react'
import { Popover, Select, Space, Tag, Typography, message } from 'antd'
import { useMsal } from '@azure/msal-react'
import { EyeOutlined, FilterOutlined, MailOutlined, PlusOutlined, SearchOutlined, SwapOutlined } from '@ant-design/icons'
import Button from '../../components/common/button/button'
import Input from '../../components/common/input/input'
import Table from '../../components/common/table/table'
import {
  DSV_STATUS_OPTIONS,
  MOVEMENT_STATUS_OPTIONS,
  SOURCE_COLORS,
  formatReason,
  renderStatusTag
} from './inventoryMovementConstants'
import { fetchInventoryMovements, getApiErrorMessage } from '../../api/inventoryMovementApiService/inventoryMovementApiUtils'
import './InventoryMovement.scss'

const { Title, Text } = Typography

const DEFAULT_PAGE_SIZE = 10

const MovementListView = ({ canWrite, onNewMovement, onViewMovement, onOpenNotificationSettings }) => {
  const { instance } = useMsal()
  const [keycodeInput, setKeycodeInput] = useState('')
  const [productNameInput, setProductNameInput] = useState('')
  const [keycodeFilter, setKeycodeFilter] = useState('')
  const [productNameFilter, setProductNameFilter] = useState('')
  const [fromStatusFilter, setFromStatusFilter] = useState('')
  const [toStatusFilter, setToStatusFilter] = useState('')
  const [dsvStatusFilter, setDsvStatusFilter] = useState('')
  const [openFilter, setOpenFilter] = useState(null)
  const [pagination, setPagination] = useState({ current: 1, pageSize: DEFAULT_PAGE_SIZE })
  const [movements, setMovements] = useState([])
  const [total, setTotal] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    let isCurrent = true
    const loadMovements = async () => {
      setIsLoading(true)
      try {
        const result = await fetchInventoryMovements(instance, {
          page: pagination.current,
          limit: pagination.pageSize,
          keyCode: keycodeFilter,
          productName: productNameFilter,
          fromMovementStatus: fromStatusFilter,
          toMovementStatus: toStatusFilter,
          dsvStatus: dsvStatusFilter
        })
        if (!isCurrent) return
        setMovements(result.movements)
        setTotal(result.total)
      } catch (error) {
        if (!isCurrent) return
        setMovements([])
        setTotal(0)
        message.error(getApiErrorMessage(error, 'Failed to load inventory movements'))
      } finally {
        if (isCurrent) setIsLoading(false)
      }
    }
    loadMovements()
    return () => { isCurrent = false }
  }, [instance, pagination, keycodeFilter, productNameFilter, fromStatusFilter, toStatusFilter, dsvStatusFilter])

  // Every filter change restarts from the first page
  const applyFilter = (setter, value) => {
    setter(value)
    setPagination(current => ({ ...current, current: 1 }))
  }

  const applyKeycodeFilter = (value) => {
    setKeycodeInput(value)
    applyFilter(setKeycodeFilter, value.trim())
    setOpenFilter(null)
  }

  const applyProductNameFilter = (value) => {
    setProductNameInput(value)
    applyFilter(setProductNameFilter, value.trim())
    setOpenFilter(null)
  }

  const renderStatusFilter = (filterKey, label, value, setter, options) => (
    <span className="inventory-movement__filter-header">
      {label}
      <Popover
        trigger="click"
        placement="bottomLeft"
        open={openFilter === filterKey}
        onOpenChange={visible => setOpenFilter(visible ? filterKey : null)}
        content={(
          <div className="inventory-movement__filter-panel">
            <Select
              placeholder="All statuses"
              allowClear
              className="inventory-movement__filter-select"
              value={value || undefined}
              options={options.map(option => ({ value: option, label: option }))}
              onChange={newValue => applyFilter(setter, newValue || '')}
            />
            <div className="inventory-movement__filter-actions">
              <Button size="small" onClick={() => { applyFilter(setter, ''); setOpenFilter(null) }}>Reset</Button>
              <Button type="primary" size="small" onClick={() => setOpenFilter(null)}>OK</Button>
            </div>
          </div>
        )}
      >
        <FilterOutlined
          className={`inventory-movement__filter-icon${value ? ' inventory-movement__filter-icon--active' : ''}`}
          onClick={event => event.stopPropagation()}
        />
      </Popover>
    </span>
  )

  const columns = [
    {
      title: 'Movement',
      dataIndex: 'reference',
      key: 'reference',
      width: 130,
      render: (reference, record) => (
        <div
          className="inventory-movement__reference inventory-movement__reference--clickable"
          role="button"
          tabIndex={0}
          onClick={() => onViewMovement(record.id)}
          onKeyDown={(event) => { if (event.key === 'Enter') onViewMovement(record.id) }}
        >
          <strong>{reference}</strong>
          <span>{record.products.length} keycode{record.products.length > 1 ? 's' : ''}</span>
          {record.source && <Tag color={SOURCE_COLORS[record.source]}>{record.source}</Tag>}
        </div>
      )
    },
    {
      title: (
        <div className="inventory-movement__product-columns inventory-movement__product-columns--header">
          <span className="inventory-movement__filter-header">
            Keycode
            <Popover
              trigger="click"
              placement="bottomLeft"
              open={openFilter === 'keycode'}
              onOpenChange={visible => {
                setOpenFilter(visible ? 'keycode' : null)
                if (visible) setKeycodeInput(keycodeFilter)
              }}
              content={(
                <div className="inventory-movement__filter-panel">
                  <Input
                    placeholder="Search keycode"
                    value={keycodeInput}
                    autoFocus
                    maxLength={50}
                    className="inventory-movement__filter-input"
                    onChange={event => setKeycodeInput(event.target.value)}
                    onPressEnter={() => applyKeycodeFilter(keycodeInput)}
                  />
                  <div className="inventory-movement__filter-actions">
                    <Button size="small" onClick={() => applyKeycodeFilter('')}>Reset</Button>
                    <Button type="primary" size="small" icon={<SearchOutlined />} onClick={() => applyKeycodeFilter(keycodeInput)}>Search</Button>
                  </div>
                </div>
              )}
            >
              <SearchOutlined
                className={`inventory-movement__filter-icon${keycodeFilter ? ' inventory-movement__filter-icon--active' : ''}`}
                onClick={event => event.stopPropagation()}
              />
            </Popover>
          </span>
          <span className="inventory-movement__filter-header">
            Product Name
            <Popover
              trigger="click"
              placement="bottomLeft"
              open={openFilter === 'productName'}
              onOpenChange={visible => {
                setOpenFilter(visible ? 'productName' : null)
                if (visible) setProductNameInput(productNameFilter)
              }}
              content={(
                <div className="inventory-movement__filter-panel">
                  <Input
                    placeholder="Search product name"
                    value={productNameInput}
                    autoFocus
                    maxLength={100}
                    className="inventory-movement__filter-input"
                    onChange={event => setProductNameInput(event.target.value)}
                    onPressEnter={() => applyProductNameFilter(productNameInput)}
                  />
                  <div className="inventory-movement__filter-actions">
                    <Button size="small" onClick={() => applyProductNameFilter('')}>Reset</Button>
                    <Button type="primary" size="small" icon={<SearchOutlined />} onClick={() => applyProductNameFilter(productNameInput)}>Search</Button>
                  </div>
                </div>
              )}
            >
              <SearchOutlined
                className={`inventory-movement__filter-icon${productNameFilter ? ' inventory-movement__filter-icon--active' : ''}`}
                onClick={event => event.stopPropagation()}
              />
            </Popover>
          </span>
          <span>Quantity</span>
          {renderStatusFilter('fromStatus', 'Anko From', fromStatusFilter, setFromStatusFilter, MOVEMENT_STATUS_OPTIONS)}
          {renderStatusFilter('toStatus', 'Anko To', toStatusFilter, setToStatusFilter, MOVEMENT_STATUS_OPTIONS)}
          <span>DSV From</span>
          {renderStatusFilter('dsvStatus', 'DSV To', dsvStatusFilter, setDsvStatusFilter, DSV_STATUS_OPTIONS)}
          <span>DSV Quantity</span>
          <span>Reason</span>
        </div>
      ),
      dataIndex: 'products',
      key: 'products',
      width: 1270,
      render: products => (
        <div className="inventory-movement__product-list">
          {products.map(product => (
            <div className="inventory-movement__product-item inventory-movement__product-columns" key={product.keycode}>
              <span className="inventory-movement__keycode">{product.keycode}</span>
              <span className="inventory-movement__product-name">{product.productName}</span>
              <span className="inventory-movement__description">{product.quantity}</span>
              <span>{renderStatusTag(product.fromMovementStatus)}</span>
              <span>{renderStatusTag(product.toMovementStatus)}</span>
              <span>{renderStatusTag(product.dsvFromStatus)}</span>
              <span>{renderStatusTag(product.dsvStatus || 'Pending')}</span>
              <span className="inventory-movement__description">{product.dsvQuantity ?? '—'}</span>
              <span className="inventory-movement__description">{formatReason(product.reasonCode, product.reasonName) || '—'}</span>
            </div>
          ))}
        </div>
      )
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      width: 170,
      render: createdBy => <div className="inventory-movement__reference">{createdBy}</div>
    },
    {
      title: 'Created At',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: createdAt => <div className="inventory-movement__reference">{createdAt}</div>
    },
    {
      title: 'Action',
      key: 'actions',
      width: 90,
      render: (_, record) => (
        <div className="inventory-movement__reference">
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => onViewMovement(record.id)}
            aria-label={`View ${record.reference}`}
          >
            View
          </Button>
        </div>
      )
    }
  ]

  return (
    <section className="inventory-movement">
      <div className="inventory-movement__header">
        <div>
          <Space align="center" size={10}>
            <SwapOutlined className="inventory-movement__title-icon" />
            <Title level={2}>Inventory Movement</Title>
          </Space>
          <Text type="secondary">Record and review product condition and movements.</Text>
        </div>
        {canWrite && (
          <Space size={10}>
            <Button icon={<MailOutlined />} onClick={onOpenNotificationSettings}>
              Alert emails
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={onNewMovement}>
              New Movement
            </Button>
          </Space>
        )}
      </div>

      <Table
        dataSource={movements}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        scroll={{ x: 1860 }}
        pagination={{ ...pagination, total, showSizeChanger: true }}
        onChange={({ current, pageSize }) => setPagination({ current, pageSize })}
        className="inventory-movement__table"
        rowClassName="inventory-movement__group-row"
        rowHoverable={false}
      />
    </section>
  )
}

export default MovementListView
